import React, { useCallback, useEffect, useState } from 'react';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { apiAccounts } from '../../../lib/api';

export const today = () => new Date().toISOString().slice(0, 10);
export const yearStart = () => `${new Date().getFullYear()}-01-01`;
export const monthStart = () => today().slice(0, 8) + '01';

export const money = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);
/** Accounting style: negatives in brackets, zero as a dash. */
export const acct = (n) => {
  const v = Math.round((n || 0) * 100) / 100;
  if (v === 0) return '—';
  return v < 0 ? `(${money(-v)})` : money(v);
};

export const accountLabel = (a) => (a ? `${a.code ? `${a.code} · ` : ''}${a.name}` : '—');

const TYPE_ORDER = ['Bank', 'Accounts Receivable', 'Other Current Asset', 'Fixed Asset', 'Other Asset', 'Accounts Payable', 'Credit Card',
  'Other Current Liability', 'Long Term Liability', 'Equity', 'Income', 'Other Income', 'Cost of Goods Sold', 'Expense', 'Other Expense'];
export const sortAccounts = (list) => [...list].sort((a, b) =>
  String(a.code || '9999').localeCompare(String(b.code || '9999')) || TYPE_ORDER.indexOf(a.type) - TYPE_ORDER.indexOf(b.type) || String(a.name).localeCompare(String(b.name)));

export const SOURCE_LABEL = {
  invoice: 'Invoice', 'invoice-payment': 'Invoice (collected)', payment: 'Payment', expense: 'Expense', bill: 'Bill',
  'bill-payment': 'Bill payment', journal: 'Journal', payroll: 'Payroll', depreciation: 'Depreciation', bank: 'Bank deposit',
};

export function useAccounts() {
  const [accounts, setAccounts] = useState([]);
  const load = useCallback(() => {
    apiAccounts.list().then((d) => setAccounts(sortAccounts(d || []))).catch(() => setAccounts([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  return [accounts, load];
}

export const AccountSelect = ({ accounts, value, onChange, filter, placeholder = 'Choose account', className = '', testId }) => (
  <Select value={value || ''} onValueChange={onChange}>
    <SelectTrigger className={className} data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent className="max-h-80">
      {accounts.filter(filter || (() => true)).map((a) => <SelectItem key={a.id} value={a.id}>{accountLabel(a)}</SelectItem>)}
    </SelectContent>
  </Select>
);

export const DateField = ({ label, value, onChange, testId }) => (
  <div>
    <Label className="text-xs text-slate-500">{label}</Label>
    <Input type="date" value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 h-9 w-40" data-testid={testId} />
  </div>
);

export const PageTitle = ({ title, subtitle, children }) => (
  <div className="flex justify-between items-end flex-wrap gap-3">
    <div>
      <h2 className="text-2xl font-extrabold tracking-tight text-slate-900">{title}</h2>
      {subtitle ? <p className="text-slate-500 text-sm">{subtitle}</p> : null}
    </div>
    <div className="flex items-end gap-2 flex-wrap">{children}</div>
  </div>
);

export const Empty = ({ children }) => <div className="text-center py-10 text-slate-500 text-sm">{children}</div>;

/** Statement row: label left, amount right, optional indent and total styling. */
export const Line = ({ label, value, prior, bold, indent = 0, total, onClick }) => (
  <div
    className={`flex justify-between gap-4 py-2 px-4 ${total ? 'border-t-2 border-slate-300 bg-slate-50' : 'border-b border-slate-100'} ${bold ? 'font-bold' : ''} ${onClick ? 'cursor-pointer hover:bg-slate-50' : ''}`}
    style={{ paddingLeft: 16 + indent * 20 }}
    onClick={onClick}
  >
    <span className={bold ? 'text-slate-900' : 'text-slate-700'}>{label}</span>
    <span className="flex gap-6">
      {prior !== undefined ? <span className="tabular-nums text-slate-400 w-28 text-right">{acct(prior)}</span> : null}
      <span className={`tabular-nums w-28 text-right ${bold ? 'text-slate-900' : 'text-slate-700'}`}>{value}</span>
    </span>
  </div>
);

/** A table of rows to CSV. */
export function downloadCSV(name, header, rows) {
  const esc = (v) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [header, ...rows].map((r) => r.map(esc).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
}
